import { NgStyle } from '@angular/common';
import {
  AfterViewInit,
  Component,
  computed,
  ElementRef,
  inject,
  input,
  OnInit,
  output,
  QueryList,
  signal,
  viewChild,
  ViewChildren,
} from '@angular/core';
import { AudioService } from '@core/audio/audio.service';
import { Wk8TutorService } from '@core/wk8/wk8-tutor.service';
import { DragAndDropService } from '@features/drag-and-drop/drag-and-drop.service';
import { IDraggable } from '@features/drag-and-drop/drag-and-drop.types';
import { DraggableDirective } from '@features/drag-and-drop/draggable.directive';
import { LpDropTargetDirective } from '@features/lp/lp-drag-and-drop/lp-drop-target.directive';
import { Wk8AudioButtonComponent } from '@features/wk8/core/wk8-audio-button/wk8-audio-button.component';
import { Wk8AudioService } from '@features/wk8/core/wk8-audio/wk8-audio.service';
import { Wk8ClawFrontComponent } from '@features/wk8/core/wk8-claw-front/wk8-claw-front.component';
import { Wk8InputPipeComponent } from '@features/wk8/core/wk8-input-pipe/wk8-input-pipe.component';
import { Wk8PaperPieceComponent } from '@features/wk8/core/wk8-paper-piece/wk8-paper-piece.component';
import { waitForMillis } from '@shared/delay.utils';
import { Wk8CeilingComponent } from '../../core/wk8-ceiling/wk8-ceiling.component';
import { Wk8ClawBackComponent } from '../../core/wk8-claw-back/wk8-claw-back.component';
import { Vector2ElementNormalized } from '@typings/vectors';

export interface MediaResource {
  label: string;
  audio_url?: string;
  image_url?: string;
}

interface MediaDisplaySettings {
  showLabel: boolean;
  showAudio: boolean;
  showImage: boolean;
}

export interface QuizQuestion extends MediaResource {}

export interface QuizOption extends MediaResource {
  correct: boolean;
}

export interface QuizConfig {
  questionSettings: MediaDisplaySettings;
  optionSettings: MediaDisplaySettings;
  introductionDelayMs: number; //in milliseconds
}

const DEFAULT_CONFIG: QuizConfig = {
  questionSettings: {
    showLabel: true,
    showAudio: true,
    showImage: false,
  },
  optionSettings: {
    showLabel: false,
    showAudio: true,
    showImage: true,
  },
  introductionDelayMs: 1500,
};

@Component({
  selector: 'wk8-sorteren',
  imports: [
    Wk8InputPipeComponent,
    Wk8PaperPieceComponent,
    LpDropTargetDirective,
    NgStyle,
    Wk8AudioButtonComponent,
    Wk8ClawBackComponent,
    Wk8ClawFrontComponent,
    Wk8CeilingComponent,
    DraggableDirective,
  ],
  templateUrl: './wk8-sorteren.component.html',
  styleUrl: './wk8-sorteren.component.css',
})
export class Wk8SorterenComponent implements OnInit, AfterViewInit {
  // INPUTS
  config = input<QuizConfig>(DEFAULT_CONFIG);
  question = input.required<QuizQuestion>();
  options = input.required<QuizOption[]>();
  scaleMultiplier = input(1);

  // INTERNAL
  paperPieceEl = viewChild<ElementRef>('el');

  isReadingWord = signal(false);
  correctlyChosen = signal(false);
  canDrag = signal(true);
  clawOpened = signal(false);
  clawVisible = signal(true);
  questionVisible = signal(false);

  clawPosition = signal({ top: 0, left: 0 });
  clawHeightOffset = signal(0);
  pipePivot = signal<Vector2ElementNormalized>([0.6, 0] as Vector2ElementNormalized);

  @ViewChildren('audioButton') audioButtonEls!: QueryList<Wk8AudioButtonComponent>;

  audioService = inject(AudioService);
  wk8AudioService = inject(Wk8AudioService);
  wk8TutorService = inject(Wk8TutorService);
  dragDropService = inject(DragAndDropService);
  tutorService = inject(Wk8TutorService);
  readonly placeholderImageUrl = 'https://placehold.co/600x400';
  readonly placeholderAudioUrl = 'assets/laughing-baby.mp3';
  optionStates: { [key: number]: 'invisible' | 'idle' | 'correct' | 'wrong' } = {};
  resultFeedback: { message: string; type: 'correct' | 'incorrect'; visible: boolean } = {
    message: 'Initially not visible',
    type: 'incorrect',
    visible: false,
  };

  clawSize = computed(() => {
    return this.scaleMultiplier() * 1.5;
  });
  fontSize = computed(() => {
    return this.scaleMultiplier() * 72;
  });

  shuffledOptions = computed(() => {
    const shuffled = [...this.options()].sort(() => Math.random() - 0.5);
    return shuffled;
  });

  // OUTPUTS
  finished = output();

  //FUNCTIONS
  async ngOnInit(): Promise<void> {
    this.initializeQuiz();
  }

  async ngAfterViewInit(): Promise<void> {
    await waitForMillis(700); // otherwise overwhelm

    this.isReadingWord.set(true);
    await this.tutorService.showMechanic('sorteer_woorden');
    this.isReadingWord.set(false);
  }

  async initializeQuiz() {
    // set each option to invisible initially
    this.shuffledOptions().forEach((_, index) => {
      this.optionStates[index] = 'invisible';
    });
  }

  async selectOption(option: QuizOption, draggable: IDraggable) {
    if (option.correct) {
      this.clawHeightOffset.set(200);
      this.correctlyChosen.set(true);
      this.clawOpened.set(true);
      this.wk8AudioService.playPipeSuck();
      await this.wk8AudioService.playActivityCorrect();
      this.finished.emit();
    } else {
      queueMicrotask(() => this.dragDropService.clearDraggable(draggable)); // I have to put this in a timeout, I assume because otherwise the event chain goes to the service AFTER this happens and immediately overwrites the clearDraggable call here
      await this.tutorService.incorrect();
    }
  }
}
